import type { ReactElement } from 'react';
import { IconButton, Tooltip, IconButtonProps, forwardRef } from '@chakra-ui/react';
import { useEditorContext } from '../editor_context';

export const tiptapButtonStyle = {
   size: 'sm',
   mx: 'px',
   bgColor: 'gray.50',
   color: 'gray.500',
   outline: 'none',
   border: 'none',
   _hover: {
      bgColor: 'gray.100',
   },
   _focus: {
      bgColor: 'gray.100',
   },
   _active: {
      bgColor: 'gray.200',
   },
};

type TiptapButtonProps = Omit<IconButtonProps, 'aria-label'> &
   Partial<Pick<IconButtonProps, 'aria-label'>> & {
      onClick: () => void;
      isActive?: boolean;
      isDisabled?: boolean;
      icon: ReactElement;
      title: string;
      shortcut?: string;
      overrideShortcut?: string;
   };

export const TiptapButton = forwardRef<TiptapButtonProps, 'button'>(
   (
      { shortcut, overrideShortcut, title, onClick, isDisabled, ...chakraProps }: TiptapButtonProps,
      ref
   ) => {
      // Label logic originally from prosemirror-example-setup/menu/menu.js
      const hasShortcut = Boolean(shortcut || overrideShortcut);
      const modKey = navigator.userAgent.indexOf('Mac') > 0 ? '⌘' : 'ctrl+';
      const shortcutText = overrideShortcut ?? `${modKey}${shortcut}`;
      const label = hasShortcut ? `${title} (${shortcutText})` : title;

      const iconButtonProps = { isDisabled, 'aria-label': title, ...chakraProps };

      const { editor } = useEditorContext();

      if (isDisabled) {
         return <IconButton ref={ref} {...tiptapButtonStyle} {...iconButtonProps} />;
      }
      return (
         <Tooltip placement="top-start" label={label}>
            <IconButton
               ref={ref}
               {...tiptapButtonStyle}
               {...iconButtonProps}
               onClick={e => {
                  e.preventDefault();
                  // Needed to fix an issue with Android keyboard losing focus
                  // https://github.com/ueberdosis/tiptap/issues/1806#issuecomment-1061167500
                  editor.view.focus();
                  onClick();
               }}
            />
         </Tooltip>
      );
   }
);
